import React, { useContext, useEffect, useRef, useState } from "react";
import styled, { ThemeContext } from "styled-components";
import { fadeInCss } from "../styleBits";
import { Field } from "../components/Field";
import PageLink from "../components/PageLink";
import LoadError, { Loading } from "../components/LoadError";
import { useNavigationType } from "react-router-dom";
import Fuse from "fuse.js";

import { ImageEnum, imgBlob } from "../cssImg";
import { Button, ButtonTheme } from "../components/Button";
import Story from "../components/Story";
import StoriesEmptyState from "../components/StoryEmptyState";
import {
  PAGE_SIZE,
  StoryRecord,
  getLoadedStories,
  listMemory,
  loadStories,
  visibleStories,
} from "../stories";

const StoryContainer = styled.div`
  ${fadeInCss}
  margin: 64px auto;
  max-width: 800px;

  a {
    text-decoration: none;
  }

  .page-title {
    width: 100%;
    font-family: "BryantWebBold";
    font-size: 45px;
    font-style: normal;
    font-weight: 700;
    line-height: 48px;
    letter-spacing: -1px;
    color: var(--title);
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    margin: 0 0 40px;

    img {
      filter: var(--logo-fingers-filter);
    }
  }

  .load-more {
    margin-top: 24px;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 16px;

    .count {
      font-family: Nunito;
      font-size: 18px;
      font-weight: 700;
      line-height: 24px;
      color: var(--subtitle);
    }
  }
`;

const StoryList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
  margin-top: 24px;
`;

const Stories = (props) => {
  // null until the stories have arrived; seeded from the cache when
  // the visitor has already been on a story page.
  const [original, setOriginal] = useState<StoryRecord[] | null>(() => {
    const cached = getLoadedStories();
    return cached ? visibleStories(cached) : null;
  });
  const [stories, setStories] = useState<StoryRecord[]>(original ?? []);
  // Back/forward (POP) returns to the list as it was left; a fresh link is a
  // fresh list. The initial load is also POP, but nothing is remembered yet.
  const remembered = useNavigationType() === "POP";
  const [search, setSearch] = useState(remembered ? listMemory.search : "");
  const [shown, setShown] = useState(
    remembered ? listMemory.shown : PAGE_SIZE
  );
  const [failed, setFailed] = useState(false);
  // Set by "Try again": when the list then arrives, focus goes to the page
  // content instead of being lost with the unmounted button.
  const [retried, setRetried] = useState(false);
  const listRef = useRef<HTMLDivElement>(null);
  const focusIndex = useRef<number | null>(null);
  const theme: any = useContext(ThemeContext);

  useEffect(() => {
    if (original || failed) return;
    let cancelled = false;
    loadStories().then(
      (data) => {
        if (!cancelled) setOriginal(visibleStories(data));
      },
      () => {
        if (!cancelled) setFailed(true);
      }
    );
    return () => {
      cancelled = true;
    };
  }, [original, failed]);

  // After "Load more", keyboard focus goes to the first story that appeared,
  // so the reader lands on the new content instead of on a vanished button.
  useEffect(() => {
    if (focusIndex.current === null) return;
    const links =
      listRef.current?.querySelectorAll<HTMLAnchorElement>(":scope > a");
    links?.[focusIndex.current]?.focus();
    focusIndex.current = null;
  }, [shown]);

  useEffect(() => {
    if (!retried || original === null) return;
    document.getElementById("content")?.focus({ preventScroll: true });
    setRetried(false);
  }, [original, retried]);

  useEffect(() => {
    listMemory.search = search;
    listMemory.shown = shown;
  }, [search, shown]);

  useEffect(() => {
    if (search.length === 0) {
      setStories(original ?? []);
    } else {
      const fuzzy = new Fuse(original ?? [], {
        keys: ["title", "author", "html"],
      });
      const results = fuzzy.search(search).map((result) => result.item);
      setStories(results);
    }
  }, [original, search]);

  // The visible slice follows whatever list is currently shown, so a search
  // starts back at the first few results.
  const shownStories = stories.slice(0, shown);
  const hasMore = shown < stories.length;

  // One live region for the whole list: result count while searching,
  // paging progress otherwise, nothing when everything already fits.
  const plural = (n: number) => (n === 1 ? "" : "s");
  const status =
    search !== ""
      ? stories.length === 0
        ? "No search results"
        : hasMore
        ? `${stories.length} search results, showing ${shownStories.length}`
        : `${stories.length} search result${plural(stories.length)}`
      : stories.length > PAGE_SIZE
      ? hasMore
        ? `Showing ${shownStories.length} of ${stories.length} stories`
        : `Showing all ${stories.length} stories`
      : "";

  if (original === null && !failed) {
    return (
      <StoryContainer theme={theme}>
        <Loading what="stories" />
      </StoryContainer>
    );
  }

  return (
    <StoryContainer theme={theme}>
      <h1 className="page-title">
        <img
          role="presentation"
          className="logo"
          width={90}
          height={80}
          src={imgBlob(ImageEnum.logoFingers)}
          alt="Logo"
        />
        SignUp Stories
      </h1>
      <Field label="">
        <input
          type="text"
          value={search}
          onChange={(e) => {
            setSearch(e.target.value);
            setShown(PAGE_SIZE);
          }}
          placeholder="Search Stories"
        />
      </Field>
      {search !== "" ? <h2 className="sr-only">Search Results</h2> : null}
      {failed ? (
        <LoadError
          what="The stories"
          onRetry={() => {
            setFailed(false);
            setRetried(true);
          }}
        />
      ) : (
        <>
          <StoryList ref={listRef}>
            {shownStories.map((story) => {
              return (
                <PageLink key={story._id} to={`/story/${story._id}`}>
                  <Story preview={true} story={story} />
                </PageLink>
              );
            })}
            {stories.length === 0 ? (
              <StoriesEmptyState search={search !== ""} />
            ) : null}
          </StoryList>
          <div className="load-more">
            <div className="count" role="status">
              {status}
            </div>
            {hasMore ? (
              <Button
                button
                theme={ButtonTheme.light_outline}
                text="Load more"
                onClick={() => {
                  focusIndex.current = shown;
                  setShown(shown + PAGE_SIZE);
                }}
              />
            ) : null}
          </div>
        </>
      )}
    </StoryContainer>
  );
};

export default Stories;
